import { isIntakeFieldValue } from './fieldPersistence';
import type { GeneratedIntakeField, IntakeFieldSuggestion, IntakeFieldValue } from './types';

export const REPEAT_RECORDS_KEY = '_repeat_records';

export interface IntakeRepeatRecord {
  record_id: string;
  order: number;
  values: Record<string, IntakeFieldValue>;
}

export interface IntakeRepeatGroupState {
  acknowledgedRecords: IntakeRepeatRecord[];
  draftRecords: IntakeRepeatRecord[];
}

export type IntakeRepeatGroupStates = Record<string, IntakeRepeatGroupState>;

export function repeatRecordsEqual(
  left: IntakeRepeatRecord[],
  right: IntakeRepeatRecord[],
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function reconcileRepeatGroupStates(
  serverRecordsByGroup: Record<string, IntakeRepeatRecord[]>,
  previous: IntakeRepeatGroupStates,
): IntakeRepeatGroupStates {
  return Object.fromEntries(
    Object.entries(serverRecordsByGroup).map(([groupKey, serverRecords]) => {
      const existing = previous[groupKey];
      if (!existing) {
        return [groupKey, { acknowledgedRecords: serverRecords, draftRecords: serverRecords }];
      }
      const draftIsDirty = !repeatRecordsEqual(existing.draftRecords, existing.acknowledgedRecords);
      return [
        groupKey,
        {
          acknowledgedRecords: serverRecords,
          draftRecords: draftIsDirty ? existing.draftRecords : serverRecords,
        },
      ];
    }),
  );
}

export function repeatGroupKey(field: GeneratedIntakeField): string {
  return `${field.view}:${field.requirement_key ?? ''}:${field.group_id}`;
}

function legacyRecordId(requirementKey: string): string {
  const slug = requirementKey
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `legacy-${slug || 'record'}`;
}

function normalizedStoredRecords(value: unknown): IntakeRepeatRecord[] {
  if (!Array.isArray(value)) return [];
  return value
    .flatMap((entry, index) => {
      if (!entry || typeof entry !== 'object') return [];
      const raw = entry as Record<string, unknown>;
      if (typeof raw.record_id !== 'string' || !raw.record_id) return [];
      if (!raw.values || typeof raw.values !== 'object' || Array.isArray(raw.values)) return [];
      const values = Object.fromEntries(
        Object.entries(raw.values).filter((pair): pair is [string, IntakeFieldValue] =>
          isIntakeFieldValue(pair[1]),
        ),
      );
      return [{ record_id: raw.record_id, order: Number(raw.order) || index, values }];
    })
    .sort((left, right) => left.order - right.order)
    .map((record, order) => ({ ...record, order }));
}

export function hydrateRepeatRecords(
  extractedFields: Record<string, unknown>,
  fieldKeys: string[],
  requirementKey: string,
): IntakeRepeatRecord[] {
  const stored = normalizedStoredRecords(extractedFields[REPEAT_RECORDS_KEY]);
  if (stored.length > 0) return stored;

  const values = Object.fromEntries(
    fieldKeys.flatMap(fieldKey => {
      const value = extractedFields[fieldKey];
      return isIntakeFieldValue(value) ? [[fieldKey, value] as const] : [];
    }),
  );
  return [{ record_id: legacyRecordId(requirementKey), order: 0, values }];
}

export function addRepeatRecord(
  records: IntakeRepeatRecord[],
  recordId: string,
): IntakeRepeatRecord[] {
  return [...records, { record_id: recordId, order: records.length, values: {} }];
}

export function updateRepeatRecord(
  records: IntakeRepeatRecord[],
  recordId: string,
  fieldKey: string,
  value: IntakeFieldValue,
): IntakeRepeatRecord[] {
  return records.map(record =>
    record.record_id === recordId
      ? { ...record, values: { ...record.values, [fieldKey]: value } }
      : record,
  );
}

export function removeRepeatRecord(
  records: IntakeRepeatRecord[],
  recordId: string,
): IntakeRepeatRecord[] {
  if (records[0]?.record_id === recordId) return records;
  return records
    .filter(record => record.record_id !== recordId)
    .map((record, order) => ({ ...record, order }));
}

export function repeatRecordsEvidence(records: IntakeRepeatRecord[]): Record<string, unknown> {
  return {
    [REPEAT_RECORDS_KEY]: records,
    ...(records[0]?.values ?? {}),
  };
}

export function duplicateRepeatRecordError(
  records: IntakeRepeatRecord[],
  identityFieldKey: string,
  message = 'Each value can only be added once.',
): string | undefined {
  const seen = new Set<string>();
  for (const record of records) {
    const value = record.values[identityFieldKey];
    if (typeof value !== 'string' || !value.trim()) continue;
    const normalized = value.trim().toLowerCase();
    if (seen.has(normalized)) return message;
    seen.add(normalized);
  }
  return undefined;
}

export interface RepeatDuplicatePolicy {
  fieldKey: string;
  message: string;
}

export function repeatDuplicatePolicy(
  fields: GeneratedIntakeField[],
): RepeatDuplicatePolicy | undefined {
  const scope = fields[0]?.repeat_scope?.toLowerCase();
  if (scope === 'per-state') {
    const stateField = fields.find(field => field.destination.field_key.includes('state'));
    return stateField
      ? {
          fieldKey: stateField.destination.field_key,
          message: 'Each state can only be added once.',
        }
      : undefined;
  }
  if (scope === 'per-registration') {
    const registrationField = fields.find(field =>
      /number|registration/.test(field.destination.field_key),
    );
    return registrationField
      ? {
          fieldKey: registrationField.destination.field_key,
          message: 'Each registration number can only be added once.',
        }
      : undefined;
  }
  return undefined;
}

export function repeatRecordIdForSuggestion(
  records: IntakeRepeatRecord[],
  _fieldKey: string,
  suggestion: IntakeFieldSuggestion,
  primaryOnly = false,
): string | undefined {
  const recordId = suggestion.repeat_record_id;
  if (!recordId) return undefined;
  if (primaryOnly && records[0]?.record_id !== recordId) return undefined;
  return records.filter(record => record.record_id === recordId).length === 1
    ? recordId
    : undefined;
}
